/**
 * 章节HTTP API服务
 *
 * 封装章节相关的REST API调用，用于章节CRUD操作。
 * 使用 HttpOnly Cookie 进行认证，浏览器自动携带 Token。
 */

import type { Chapter, ChapterCreate, ChapterUpdate } from '../types/chapter';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

export class HttpError extends Error {
  constructor(
    message: string,
    public status: number,
    public code?: string
  ) {
    super(message);
    this.name = 'HttpError';
  }
}

export class UnauthorizedError extends HttpError {
  constructor(message: string = '未授权，请重新登录') {
    super(message, 401, 'UNAUTHORIZED');
    this.name = 'UnauthorizedError';
  }
}

export class NotFoundError extends HttpError {
  constructor(message: string = '资源不存在') {
    super(message, 404, 'NOT_FOUND');
    this.name = 'NotFoundError';
  }
}

interface ChapterListResponse {
  chapters: Chapter[];
  total: number;
}

interface VersionListResponse {
  versions: Array<{
    id: number;
    chapter_id: number;
    version: number;
    content: string;
    word_count: number;
    editor_type: 'user' | 'agent';
    editor_name: string | null;
    created_at: string;
  }>;
  total: number;
}

interface DiffResponse {
  version: number;
  additions: number;
  deletions: number;
  diff: string;
}

function getHeaders(): HeadersInit {
  return {
    'Content-Type': 'application/json',
  };
}

async function handleResponseError(response: Response): Promise<never> {
  let errorMessage = '请求失败';

  try {
    const data = await response.json();
    errorMessage = data.detail || data.message || errorMessage;
  } catch {
  }

  switch (response.status) {
    case 401:
      throw new UnauthorizedError(errorMessage);
    case 404:
      throw new NotFoundError(errorMessage);
    default:
      throw new HttpError(errorMessage, response.status);
  }
}

export class ChapterApiService {
  private baseUrl: string;

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl;
  }

  async listChapters(
    workspaceId: number,
    branchId?: number | null,
    includeCommon: boolean = true
  ): Promise<Chapter[]> {
    const params = new URLSearchParams();
    if (branchId !== undefined && branchId !== null) {
      params.append('branch_id', String(branchId));
    }
    params.append('include_common', String(includeCommon));

    const response = await fetch(
      `${this.baseUrl}/api/chapters/workspaces/${workspaceId}?${params.toString()}`,
      {
        method: 'GET',
        headers: getHeaders(),
        credentials: 'include',
      }
    );

    if (!response.ok) {
      await handleResponseError(response);
    }

    const data: ChapterListResponse = await response.json();
    return data.chapters;
  }

  async createChapter(workspaceId: number, data: ChapterCreate): Promise<Chapter> {
    const response = await fetch(
      `${this.baseUrl}/api/chapters/workspaces/${workspaceId}`,
      {
        method: 'POST',
        headers: getHeaders(),
        credentials: 'include',
        body: JSON.stringify(data),
      }
    );

    if (!response.ok) {
      await handleResponseError(response);
    }

    return await response.json();
  }

  async getChapter(chapterId: number): Promise<Chapter> {
    const response = await fetch(
      `${this.baseUrl}/api/chapters/${chapterId}`,
      {
        method: 'GET',
        headers: getHeaders(),
        credentials: 'include',
      }
    );

    if (!response.ok) {
      await handleResponseError(response);
    }

    return await response.json();
  }

  async updateChapter(chapterId: number, data: ChapterUpdate): Promise<Chapter> {
    const response = await fetch(
      `${this.baseUrl}/api/chapters/${chapterId}`,
      {
        method: 'PUT',
        headers: getHeaders(),
        credentials: 'include',
        body: JSON.stringify(data),
      }
    );

    if (!response.ok) {
      await handleResponseError(response);
    }

    return await response.json();
  }

  async deleteChapter(chapterId: number): Promise<void> {
    const response = await fetch(
      `${this.baseUrl}/api/chapters/${chapterId}`,
      {
        method: 'DELETE',
        headers: getHeaders(),
        credentials: 'include',
      }
    );

    if (!response.ok) {
      await handleResponseError(response);
    }
  }

  async lockText(chapterId: number, ranges: Array<{ start: number; end: number }>): Promise<Chapter> {
    const response = await fetch(
      `${this.baseUrl}/api/chapters/${chapterId}/lock`,
      {
        method: 'POST',
        headers: getHeaders(),
        credentials: 'include',
        body: JSON.stringify({ ranges }),
      }
    );

    if (!response.ok) {
      await handleResponseError(response);
    }

    return await response.json();
  }

  async lockChapter(chapterId: number, action: 'lock' | 'unlock'): Promise<Chapter> {
    const response = await fetch(
      `${this.baseUrl}/api/chapters/${chapterId}/lock`,
      {
        method: 'POST',
        headers: getHeaders(),
        credentials: 'include',
        body: JSON.stringify({ action }),
      }
    );

    if (!response.ok) {
      await handleResponseError(response);
    }

    return await response.json();
  }

  async getHistory(chapterId: number): Promise<VersionListResponse> {
    const response = await fetch(
      `${this.baseUrl}/api/chapters/${chapterId}/history`,
      {
        method: 'GET',
        headers: getHeaders(),
        credentials: 'include',
      }
    );

    if (!response.ok) {
      await handleResponseError(response);
    }

    return await response.json();
  }

  async getDiff(chapterId: number, version: number): Promise<DiffResponse> {
    const response = await fetch(
      `${this.baseUrl}/api/chapters/${chapterId}/diff/${version}`,
      {
        method: 'GET',
        headers: getHeaders(),
        credentials: 'include',
      }
    );

    if (!response.ok) {
      await handleResponseError(response);
    }

    return await response.json();
  }

  async rollback(chapterId: number, version: number): Promise<Chapter> {
    const response = await fetch(
      `${this.baseUrl}/api/chapters/${chapterId}/rollback`,
      {
        method: 'POST',
        headers: getHeaders(),
        credentials: 'include',
        body: JSON.stringify({ version }),
      }
    );

    if (!response.ok) {
      await handleResponseError(response);
    }

    return await response.json();
  }

  async generateAbstract(chapterId: number): Promise<void> {
    const response = await fetch(
      `${this.baseUrl}/api/chapters/${chapterId}/abstract`,
      {
        method: 'POST',
        headers: getHeaders(),
        credentials: 'include',
      }
    );

    if (!response.ok) {
      await handleResponseError(response);
    }
  }
}

export const chapterApi = new ChapterApiService();

export default chapterApi;
